import type { UserProfileItem } from "./api";

export const REQUIRED_PROFILE_KEYS = ["name", "communication_style"] as const;

export type RequiredProfileKey = (typeof REQUIRED_PROFILE_KEYS)[number];

export type ProfileStepId = "about" | "business" | "communication" | "life";

export type ProfileInputKind = "text" | "textarea" | "singleChoice" | "multiChoice";

export type ProfileFieldKey =
  | "name"
  | "communication_style"
  | "role_title"
  | "industry"
  | "business_content"
  | "business_phase"
  | "team_size"
  | "location"
  | "home_area"
  | "work_area"
  | "frequent_places"
  | "business_interests"
  | "current_focus"
  | "current_challenges"
  | "priority_topics"
  | "hobbies"
  | "dietary_preferences"
  | "restaurant_preferences";

export type ProfileStepDefinition = {
  id: ProfileStepId;
  title: string;
  navLabel: string;
  subtitle: string;
  note: string;
};

export type ProfileFieldDefinition = {
  key: ProfileFieldKey;
  stepId: ProfileStepId;
  label: string;
  kind: ProfileInputKind;
  required?: boolean;
  recommended?: boolean;
  secondary?: boolean;
  placeholder?: string;
  maxLength?: number;
  options?: readonly string[];
  playfulOptions?: readonly string[];
  displayOrder: number;
};

export const PROFILE_STEPS: readonly ProfileStepDefinition[] = [
  {
    id: "about",
    title: "あなたについて",
    navLabel: "あなたについて",
    subtitle: "WizMateのキャラクターがあなたを自然に呼べるように、最小限だけ教えてください。",
    note: "あとで変更できます。",
  },
  {
    id: "business",
    title: "事業について",
    navLabel: "事業について",
    subtitle: "事業背景が少しあると、相談の返し方がぐっと合わせやすくなります。",
    note: "このステップはスキップできます。",
  },
  {
    id: "communication",
    title: "話し方の好み",
    navLabel: "話し方",
    subtitle: "通常チャットでのキャラクターの距離感を選んでください。",
    note: "あとでいつでも変更できます。",
  },
  {
    id: "life",
    title: "秘書・生活文脈",
    navLabel: "秘書・生活文脈",
    subtitle: "雑談や会食、移動の相談で使える前提情報です。",
    note: "すべてスキップできます。",
  },
] as const;

export const COMMUNICATION_STYLE_OPTIONS = [
  "フランク",
  "やさしい",
  "ロジカル",
  "丁寧な敬語",
  "絵文字多め",
] as const;

export const PLAYFUL_COMMUNICATION_STYLE_OPTIONS = [
  "銀座のママ風",
  "関西のおばちゃん風",
  "オタク友達風",
  "ギャル語",
  "おじさん構文",
  "丁寧なメイド風",
  "全肯定聖母",
] as const;

// 未選択・旧値・不明値はこの既定スタイルに寄せる（画面が壊れないようにするため）。
export const DEFAULT_COMMUNICATION_STYLE = "フランク";

const COMMUNICATION_STYLE_OPTION_SET = new Set<string>([
  ...COMMUNICATION_STYLE_OPTIONS,
  ...PLAYFUL_COMMUNICATION_STYLE_OPTIONS,
]);

export function isPlayfulCommunicationStyle(value: string | null | undefined): boolean {
  return (PLAYFUL_COMMUNICATION_STYLE_OPTIONS as readonly string[]).includes((value ?? "").trim());
}

// 既知の選択肢ならそのまま、未選択・旧値・不明値なら既定スタイルを返す。
export function resolveCommunicationStyle(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return COMMUNICATION_STYLE_OPTION_SET.has(trimmed) ? trimmed : DEFAULT_COMMUNICATION_STYLE;
}

const ROLE_TITLE_OPTIONS = [
  "経営者・代表",
  "役員",
  "個人事業主",
  "部門責任者",
  "マネージャー",
  "担当者",
  "その他",
] as const;

const AREA_OPTIONS = ["東京", "大阪", "名古屋", "福岡", "全国", "オンライン中心"] as const;

const LOCAL_AREA_OPTIONS = [
  "新宿",
  "渋谷",
  "東京駅周辺",
  "品川",
  "中野",
  "神田",
  "横浜",
  "大阪",
  "オンライン中心",
] as const;

const INDUSTRY_OPTIONS = [
  "IT・システム",
  "士業",
  "小売",
  "飲食",
  "建設・不動産",
  "医療・介護",
  "教育",
  "製造",
  "コンサルティング",
  "その他",
] as const;

const BUSINESS_PHASE_OPTIONS = [
  "立ち上げ中",
  "拡大中",
  "安定運用",
  "事業承継",
  "新規事業検討中",
  "その他",
] as const;

const TEAM_SIZE_OPTIONS = ["1人", "2〜5人", "6〜10人", "11〜30人", "31〜100人", "100人以上"] as const;

const CURRENT_FOCUS_OPTIONS = [
  "売上拡大",
  "採用",
  "業務改善",
  "AI活用",
  "新規事業",
  "資金繰り",
  "組織づくり",
  "集客",
  "顧客対応",
  "その他",
] as const;

const BUSINESS_INTEREST_OPTIONS = [
  "AI活用",
  "DX",
  "営業",
  "マーケティング",
  "採用",
  "資金調達",
  "補助金",
  "新規事業",
  "組織づくり",
  "業務改善",
] as const;

const CHALLENGE_OPTIONS = [
  "売上が伸びない",
  "採用が難しい",
  "業務が属人化",
  "時間が足りない",
  "資金繰り",
  "集客",
  "顧客対応",
  "意思決定",
] as const;

const HOBBY_OPTIONS = [
  "キャンプ",
  "旅行",
  "野球",
  "ゴルフ",
  "ゲーム",
  "映画",
  "音楽",
  "サウナ",
  "グルメ",
  "読書",
  "釣り",
] as const;

const FOOD_OPTIONS = [
  "和食",
  "焼肉",
  "寿司",
  "イタリアン",
  "中華",
  "ラーメン",
  "静かな店",
  "個室",
  "駅近",
  "カジュアル",
  "会食向き",
  "ひとりで入りやすい",
] as const;

const RESTAURANT_OPTIONS = [
  "静かに話せる",
  "個室がよい",
  "駅から近い",
  "予算控えめ",
  "少し高級感",
  "予約しやすい",
] as const;

export const PROFILE_FIELD_DEFINITIONS: readonly ProfileFieldDefinition[] = [
  {
    key: "name",
    stepId: "about",
    label: "呼ばれ方",
    kind: "text",
    required: true,
    placeholder: "例: 田中さん",
    maxLength: 60,
    displayOrder: 10,
  },
  {
    key: "role_title",
    stepId: "about",
    label: "立場・肩書き",
    kind: "text",
    options: ROLE_TITLE_OPTIONS,
    placeholder: "例: 代表、店舗責任者",
    maxLength: 80,
    displayOrder: 20,
  },
  {
    key: "location",
    stepId: "about",
    label: "活動エリア",
    kind: "text",
    options: AREA_OPTIONS,
    placeholder: "例: 東京、全国、オンライン中心",
    maxLength: 80,
    displayOrder: 30,
  },
  {
    key: "industry",
    stepId: "business",
    label: "業種・業界",
    kind: "text",
    recommended: true,
    options: INDUSTRY_OPTIONS,
    placeholder: "例: IT・システム",
    maxLength: 80,
    displayOrder: 40,
  },
  {
    key: "business_content",
    stepId: "business",
    label: "事業内容",
    kind: "textarea",
    recommended: true,
    placeholder: "例: 中小企業向けに業務システムを開発しています",
    maxLength: 180,
    displayOrder: 50,
  },
  {
    key: "current_focus",
    stepId: "business",
    label: "今注力していること",
    kind: "multiChoice",
    recommended: true,
    options: CURRENT_FOCUS_OPTIONS,
    placeholder: "任意メモ",
    maxLength: 160,
    displayOrder: 60,
  },
  {
    key: "business_phase",
    stepId: "business",
    label: "事業フェーズ",
    kind: "text",
    secondary: true,
    options: BUSINESS_PHASE_OPTIONS,
    placeholder: "例: 拡大中",
    maxLength: 80,
    displayOrder: 70,
  },
  {
    key: "team_size",
    stepId: "business",
    label: "チーム規模",
    kind: "text",
    secondary: true,
    options: TEAM_SIZE_OPTIONS,
    placeholder: "例: 6〜10人",
    maxLength: 80,
    displayOrder: 80,
  },
  {
    key: "current_challenges",
    stepId: "business",
    label: "今の課題",
    kind: "multiChoice",
    secondary: true,
    options: CHALLENGE_OPTIONS,
    placeholder: "任意メモ",
    maxLength: 160,
    displayOrder: 90,
  },
  {
    key: "business_interests",
    stepId: "business",
    label: "関心テーマ",
    kind: "multiChoice",
    secondary: true,
    options: BUSINESS_INTEREST_OPTIONS,
    placeholder: "任意メモ",
    maxLength: 160,
    displayOrder: 100,
  },
  {
    key: "priority_topics",
    stepId: "business",
    label: "優先テーマ",
    kind: "multiChoice",
    secondary: true,
    options: BUSINESS_INTEREST_OPTIONS,
    placeholder: "任意メモ",
    maxLength: 160,
    displayOrder: 110,
  },
  {
    key: "communication_style",
    stepId: "communication",
    label: "話し方の好み",
    kind: "singleChoice",
    required: true,
    options: COMMUNICATION_STYLE_OPTIONS,
    playfulOptions: PLAYFUL_COMMUNICATION_STYLE_OPTIONS,
    displayOrder: 120,
  },
  {
    key: "home_area",
    stepId: "life",
    label: "生活エリア",
    kind: "text",
    options: LOCAL_AREA_OPTIONS,
    placeholder: "例: 中野、横浜",
    maxLength: 80,
    displayOrder: 130,
  },
  {
    key: "work_area",
    stepId: "life",
    label: "主な仕事エリア",
    kind: "text",
    options: LOCAL_AREA_OPTIONS,
    placeholder: "例: 東京駅周辺、オンライン中心",
    maxLength: 80,
    displayOrder: 140,
  },
  {
    key: "frequent_places",
    stepId: "life",
    label: "よく行く場所",
    kind: "multiChoice",
    options: LOCAL_AREA_OPTIONS,
    placeholder: "任意メモ",
    maxLength: 160,
    displayOrder: 150,
  },
  {
    key: "hobbies",
    stepId: "life",
    label: "趣味・好きなこと",
    kind: "multiChoice",
    options: HOBBY_OPTIONS,
    placeholder: "任意メモ",
    maxLength: 160,
    displayOrder: 160,
  },
  {
    key: "dietary_preferences",
    stepId: "life",
    label: "食事やお店の好み",
    kind: "multiChoice",
    options: FOOD_OPTIONS,
    placeholder: "任意メモ",
    maxLength: 160,
    displayOrder: 170,
  },
  {
    key: "restaurant_preferences",
    stepId: "life",
    label: "会食・店選びの好み",
    kind: "multiChoice",
    options: RESTAURANT_OPTIONS,
    placeholder: "任意メモ",
    maxLength: 160,
    displayOrder: 180,
  },
] as const;

const PROFILE_FIELD_KEYS = new Set<string>(PROFILE_FIELD_DEFINITIONS.map((field) => field.key));

export function isProfileFieldKey(value: string): value is ProfileFieldKey {
  return PROFILE_FIELD_KEYS.has(value);
}

export function getProfileValue(profiles: readonly UserProfileItem[], key: string): string {
  return profiles.find((profile) => profile.key === key)?.value.trim() ?? "";
}

export function hasRequiredProfileValues(profiles: readonly UserProfileItem[]): boolean {
  return REQUIRED_PROFILE_KEYS.every((key) => getProfileValue(profiles, key).length > 0);
}

export function hasRequiredProfileValueMap(values: Partial<Record<ProfileFieldKey, string>>): boolean {
  return REQUIRED_PROFILE_KEYS.every((key) => (values[key] ?? "").trim().length > 0);
}

export function getInitialProfileStepIndex(values: Partial<Record<ProfileFieldKey, string>>): number {
  if (!(values.name ?? "").trim()) return 0;
  if (!(values.communication_style ?? "").trim()) return 2;
  return 0;
}

export function splitProfileListValue(value: string): string[] {
  return value
    .split(/[、,\n]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function joinProfileListValue(values: readonly string[]): string {
  return Array.from(new Set(values.map((item) => item.trim()).filter((item) => item.length > 0))).join("、");
}
